/**
 * The living circle (Adam, 13 Sep): while a turn runs, one circle sits in a
 * fixed place astride the composer's top edge with the orb inside it. It
 * never moves and never shows words of its own; the words are in the
 * tooltip, as the turn's activity log, where a growing list does not jump
 * the way a replaced line did. State changes cross-fade rather than cut.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import ActivityOrb, { type OrbState } from './ActivityOrb.js'
import type { ActivityLog, ActivityLogEntry } from '../hooks/useActivityLog.js'
import { HAIRLINE } from './surface.js'

/** A FAB's size (Adam, 13 Sep: "a tiny bit bigger"): the 64 design scaled to 48 keeps the dots readable. */
export const ACTIVITY_BADGE_SIZE = 56
const ORB_DESIGN = 64
const ORB_BOX = ACTIVITY_BADGE_SIZE - 8
const FADE_MS = 320
/** At rest between turns the orb is presence, not activity: quieter and slow
 *  (Adam, 13 Sep), but not faint (Adam, 15 Sep: "less faint, and when hovered
 *  not at all") — under the pointer or focus it is drawn in full. */
export const RESTING_INK = 0.7
const RESTING_SPEED = 0.6
const REST_FADE_MS = 600

interface Layer { key: number; state: OrbState }

/** The orb with a cross-fade on every state change: the old layer fades under the new. */
function CrossfadeOrb({ state, theme, resting }: { state: OrbState; theme?: 'light' | 'dark'; resting: boolean }) {
  const [layers, setLayers] = useState<Layer[]>([{ key: 0, state }])
  const seq = useRef(0)
  useEffect(() => {
    setLayers(prev => (prev[prev.length - 1]?.state === state ? prev : [...prev.slice(-1), { key: ++seq.current, state }]))
  }, [state])
  useEffect(() => {
    if (layers.length < 2) return undefined
    const t = setTimeout(() => setLayers(prev => prev.slice(-1)), FADE_MS)
    return () => clearTimeout(t)
  }, [layers])
  const scale = ORB_BOX / ORB_DESIGN
  return (
    <Box data-testid="activity-badge-orb" className="activity-badge-orb" sx={{ position: 'relative', width: ORB_BOX, height: ORB_BOX, opacity: resting ? RESTING_INK : 1, transition: `opacity ${REST_FADE_MS}ms ease` }}>
      {layers.map((l, i) => (
        <Box key={l.key} sx={{ position: 'absolute', inset: 0, opacity: i === layers.length - 1 ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}>
          <ActivityOrb state={l.state} size={64} theme={theme} speed={resting ? RESTING_SPEED : 1} style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }} />
        </Box>
      ))}
    </Box>
  )
}

function clock(ms: number): string {
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function LogList({ activity }: { activity: ActivityLog }) {
  const entries: ActivityLogEntry[] = activity.log
  const resting = activity.current === null
  return (
    <Box data-testid="activity-log" sx={{ py: 0.5, minWidth: 260, maxWidth: 340 }}>
      {resting && (
        <Typography component="div" sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.3, pb: entries.length ? 0.5 : 0 }}>
          Agent ready{entries.length ? ' · the last turn:' : ''}
        </Typography>
      )}
      {entries.map(e => {
        const current = activity.current?.id === e.id
        return (
          <Box key={e.id} sx={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 1, alignItems: 'baseline', py: 0.25, opacity: current ? 1 : 0.6 }}>
            <Typography component="span" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{clock(e.at)}</Typography>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="div" sx={{ fontSize: 12.5, fontWeight: current ? 600 : 400, lineHeight: 1.3 }}>
                {e.label}{current && e.elapsedSeconds != null && e.elapsedSeconds > 0 ? ` · ${e.elapsedSeconds}s` : ''}
              </Typography>
              {e.detail && (
                <Typography component="div" sx={{ fontSize: 11, opacity: 0.75, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.detail}</Typography>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export interface ActivityBadgeProps {
  activity: ActivityLog
  theme?: 'light' | 'dark'
  /**
   * What the hover shows instead of the turn's activity log. The start
   * screen's circle (#1430) is the same circle with a different story behind
   * it: the sessions that are working, not the steps of one turn.
   */
  tooltip?: React.ReactNode
}

export default function ActivityBadge({ activity, theme, tooltip }: ActivityBadgeProps) {
  const c = activity.current
  const resting = c === null
  const label = c ? `${c.label}${c.detail ? ` · ${c.detail}` : ''}${c.elapsedSeconds != null && c.elapsedSeconds > 0 ? ` · ${c.elapsedSeconds}s` : ''}` : 'Agent ready'
  return (
    <Tooltip title={tooltip ?? <LogList activity={activity} />} placement="top-end" arrow enterDelay={150} leaveDelay={200}>
      <Box
        data-testid="activity-badge"
        data-orb-state={activity.orbState}
        data-resting={resting ? 'true' : 'false'}
        tabIndex={0}
        role="status"
        aria-label={label}
        sx={{
          position: 'relative',
          width: ACTIVITY_BADGE_SIZE, height: ACTIVITY_BADGE_SIZE, borderRadius: '50%',
          display: 'grid', placeItems: 'center',
          bgcolor: 'background.paper', border: HAIRLINE,
          cursor: 'default', outline: 'none',
          '&:focus-visible': { boxShadow: '0 0 0 3px rgba(59,130,246,0.35)' },
          // Attention lifts the resting orb to full ink; the hover is where its story is.
          '&:hover .activity-badge-orb, &:focus-visible .activity-badge-orb': { opacity: 1 },
        }}
      >
        <CrossfadeOrb state={activity.orbState} theme={theme} resting={resting} />
        {/* The words, for screen readers and for tests: never drawn. */}
        <Box component="span" data-testid="agent-status-line" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>{resting ? '' : label}</Box>
      </Box>
    </Tooltip>
  )
}
